<script setup>
/**
 * FormControlFilesUsage
 * Fetches and displays a list of where a specific file is being used
 * across the system (e.g., in which components or content rows).
 */
import { ref, watch, onMounted, getCurrentInstance } from 'vue';
import FormControlFilesUsageItem from './FormControlFilesUsageItem.vue';

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number, Array],
        default: null
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---

const items = ref([]);
const isLoading = ref(false);

// --- Methods ---

/**
 * Fetches the file usage data from the API.
 */
const fetchUsage = async () => {
    const fileId = props.currentData.id;

    if (!fileId) {
        items.value = [];
        return;
    }

    isLoading.value = true;
    try {
        const response = await store.dispatch('request/get', {
            url: 'api/file-usage',
            params: { fileId }
        });
        items.value = response.data?.items || [];
    } catch (error) {
        console.error('Failed to fetch file usage:', error);
        items.value = [];
    } finally {
        isLoading.value = false;
    }
};

// --- Lifecycle & Watchers ---

onMounted(() => {
    fetchUsage();
});

/**
 * Watch for changes to the file ID to ensure the list stays
 * in sync if the user switches records within the same form view.
 */
watch(() => props.currentData.id, () => {
    fetchUsage();
});
</script>

<template>
    <div class="file-usage-container">
        <ul v-if="items.length" class="list-group">
            <li v-for="item in items" :key="item.id" class="list-group-item">
                <FormControlFilesUsageItem :item="item" />
            </li>
        </ul>

        <div v-else-if="!isLoading" class="text-muted small p-2 border rounded bg-light">
            <i class="bi bi-info-circle me-1"></i>
            This file is not currently linked to any content.
        </div>

        <div v-if="isLoading" class="p-2 text-center">
            <div class="spinner-border spinner-border-sm text-secondary" role="status">
                <span class="visually-hidden">Loading usage...</span>
            </div>
        </div>
    </div>
</template>

<style scoped>
.file-usage-container {
    width: 100%;
}
</style>